/**
 * Etapa 4 - Produtos e ofertas: lista editavel (nome, preco, recorrencia,
 * provedor).
 *
 * O preco e digitado em reais como texto e guardado em CENTAVOS inteiros
 * (`reaisParaCentavos`, por string). O id de cada oferta nova e sequencial a
 * partir do maior ja existente, e nao um contador local: o contador zeraria
 * ao voltar para esta etapa e geraria id repetido. "Adicionar" e botao de
 * contorno -- o `primary` da tela e o "Continuar" do rodape.
 */

import { useState } from 'react';
import { Badge, Button, Field, Fieldset, IconButton, Input, Select } from '../../../../components/ui';
import { formatBRLFromCents } from '../../../../lib/format';
import { reaisParaCentavos, type OfertaRascunho } from '../../assistente';
import type { ProvedorIntegracao } from '../../types';
import { PROVEDORES, PROVEDOR_INFO } from '../../vocabulario';
import { erroDe, type EtapaProps } from './props';

const PREFIXO_ID = 'SIMULADO-oferta-nova-';

interface NovaOferta {
  readonly nome: string;
  readonly preco: string;
  readonly recorrente: boolean;
  readonly provider: ProvedorIntegracao;
}

const NOVA_VAZIA: NovaOferta = { nome: '', preco: '', recorrente: false, provider: 'kiwify' };

function ehProvider(valor: string): valor is ProvedorIntegracao {
  return PROVEDORES.some((p) => p === valor);
}

function proximoId(ofertas: readonly OfertaRascunho[]): string {
  const maior = ofertas.reduce((max, o) => {
    const n = Number(o.id.slice(PREFIXO_ID.length));
    return o.id.startsWith(PREFIXO_ID) && Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `${PREFIXO_ID}${maior + 1}`;
}

export function EtapaOfertas(props: EtapaProps): JSX.Element {
  const { form, onChange } = props;
  const [nova, setNova] = useState<NovaOferta>(NOVA_VAZIA);
  const [tentouAdicionar, setTentouAdicionar] = useState(false);

  const precoCents = reaisParaCentavos(nova.preco);
  const erroNome = nova.nome.trim() === '' ? 'Informe o nome da oferta.' : null;
  const erroPreco = precoCents === null || precoCents <= 0 ? 'Informe um preço em reais, como 49,70.' : null;

  const adicionar = (): void => {
    setTentouAdicionar(true);
    if (erroNome !== null || erroPreco !== null || precoCents === null) {
      return;
    }
    const oferta: OfertaRascunho = {
      id: proximoId(form.ofertas),
      nome: nova.nome.trim(),
      precoCents,
      recorrente: nova.recorrente,
      provider: nova.provider,
    };
    onChange({ ofertas: [...form.ofertas, oferta] });
    setNova({ ...NOVA_VAZIA, provider: nova.provider });
    setTentouAdicionar(false);
  };

  const remover = (id: string): void => {
    onChange({ ofertas: form.ofertas.filter((o) => o.id !== id) });
  };

  const erroLista = erroDe(props, 'ofertas');

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="Nova oferta" description="Produto, plano ou assinatura que a empresa vende. Ofertas recorrentes alimentam o funil de expirados.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Nome" required error={tentouAdicionar ? erroNome : null}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={nova.nome}
                onChange={(e) => setNova((n) => ({ ...n, nome: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    adicionar();
                  }
                }}
                autoComplete="off"
              />
            )}
          </Field>
          <Field label="Preço (R$)" required error={tentouAdicionar ? erroPreco : null}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                inputMode="decimal"
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={nova.preco}
                onChange={(e) => setNova((n) => ({ ...n, preco: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    adicionar();
                  }
                }}
                placeholder="49,70"
                autoComplete="off"
                className="tabular-nums"
              />
            )}
          </Field>
          <Field label="Tipo">
            {({ id, describedBy }) => (
              <Select id={id} aria-describedby={describedBy} value={nova.recorrente ? 'recorrente' : 'unica'} onChange={(e) => setNova((n) => ({ ...n, recorrente: e.target.value === 'recorrente' }))}>
                <option value="unica">Compra única</option>
                <option value="recorrente">Recorrente (assinatura)</option>
              </Select>
            )}
          </Field>
          <Field label="Provedor de checkout">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={nova.provider}
                onChange={(e) => {
                  const valor = e.target.value;
                  if (ehProvider(valor)) {
                    setNova((n) => ({ ...n, provider: valor }));
                  }
                }}
              >
                {PROVEDORES.map((p) => (
                  <option key={p} value={p}>
                    {PROVEDOR_INFO[p].label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <div>
          <Button variant="secondary" icon="mais" onClick={adicionar}>
            Adicionar oferta
          </Button>
        </div>
      </Fieldset>

      <section aria-labelledby="ofertas-lista-titulo" className="flex flex-col rounded-2xl border border-line bg-surface shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-5">
          <h3 id="ofertas-lista-titulo" className="text-sm font-semibold text-ink">
            Ofertas cadastradas <span className="text-ink-subtle">({form.ofertas.length})</span>
          </h3>
          {erroLista !== null ? (
            <p role="alert" className="text-xs text-status-critical">
              {erroLista}
            </p>
          ) : null}
        </div>

        {form.ofertas.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-ink-muted">Nenhuma oferta ainda. Cadastre ao menos uma para a régua saber o que está recuperando.</p>
        ) : (
          <>
            <div className="hidden overflow-x-auto pt-3 md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
                    <th scope="col" className="px-5 py-2">
                      Oferta
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      Preço
                    </th>
                    <th scope="col" className="px-3 py-2">
                      Tipo
                    </th>
                    <th scope="col" className="px-3 py-2">
                      Provedor
                    </th>
                    <th scope="col" className="px-5 py-2 text-right">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {form.ofertas.map((o) => (
                    <tr key={o.id}>
                      <td className="px-5 py-3 font-medium text-ink">{o.nome}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-ink">{formatBRLFromCents(o.precoCents)}</td>
                      <td className="px-3 py-3">
                        <Badge tone={o.recorrente ? 'info' : 'neutral'}>{o.recorrente ? 'Recorrente' : 'Compra única'}</Badge>
                      </td>
                      <td className="px-3 py-3 text-ink-muted">{PROVEDOR_INFO[o.provider].curto}</td>
                      <td className="px-5 py-3 text-right">
                        <IconButton icon="lixeira" label={`Remover ${o.nome}`} size="sm" onClick={() => remover(o.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="divide-y divide-line pt-3 md:hidden">
              {form.ofertas.map((o) => (
                <li key={o.id} className="flex items-start justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <p className="font-medium text-ink">{o.nome}</p>
                    <p className="text-sm text-ink-muted">
                      <span className="tabular-nums">{formatBRLFromCents(o.precoCents)}</span> · {PROVEDOR_INFO[o.provider].curto}
                    </p>
                    <div className="mt-1">
                      <Badge tone={o.recorrente ? 'info' : 'neutral'}>{o.recorrente ? 'Recorrente' : 'Compra única'}</Badge>
                    </div>
                  </div>
                  <IconButton icon="lixeira" label={`Remover ${o.nome}`} size="sm" onClick={() => remover(o.id)} />
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
